import { createContext, useContext, useState, useEffect } from 'react'
import {
  onAuthChange,
  loginWithEmail,
  registerWithEmail,
  logout,
  resetPassword,
  subscribeToDocument,
} from '../services/firebase'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let unsubscribeProfile = null

    const unsubscribeAuth = onAuthChange((firebaseUser) => {
      if (unsubscribeProfile) {
        unsubscribeProfile()
        unsubscribeProfile = null
      }

      if (firebaseUser) {
        setUser(firebaseUser)
        // Live subscription: picks up the profile the register page creates
        // moments after signup, and unlocks the portal the instant an admin
        // approves a pending worker. A missing doc stays null — the client
        // never invents a profile (roles are granted by admins only).
        unsubscribeProfile = subscribeToDocument('users', firebaseUser.uid, (profileDoc) => {
          setUserProfile(profileDoc)
          setLoading(false)
        })
      } else {
        setUser(null)
        setUserProfile(null)
        setLoading(false)
      }
    })

    return () => {
      unsubscribeAuth()
      if (unsubscribeProfile) unsubscribeProfile()
    }
  }, [])

  const login = async (email, password) => {
    try {
      setError(null)
      setLoading(true)
      await loginWithEmail(email, password)
    } catch (err) {
      setError(err.message)
      setLoading(false)
      throw err
    }
  }

  const register = async (email, password) => {
    try {
      setError(null)
      return await registerWithEmail(email, password)
    } catch (err) {
      setError(err.message)
      throw err
    }
  }

  const signOut = async () => {
    try {
      setError(null)
      await logout()
    } catch (err) {
      setError(err.message)
      throw err
    }
  }

  const sendPasswordReset = async (email) => {
    try {
      setError(null)
      await resetPassword(email)
    } catch (err) {
      setError(err.message)
      throw err
    }
  }

  const userRole = userProfile ? (userProfile.role || 'worker') : null
  const userStatus = userProfile?.status ?? null
  const isSuperAdmin = userRole === 'super_admin'
  const isAdmin = userRole === 'admin' || userRole === 'super_admin'
  const isEditor = userRole === 'editor' || isAdmin
  const isWorker = userRole === 'worker'
  // Staff docs predate the portal and have no `status` field — role alone
  // grants access. Workers need explicit approval.
  const hasPortalAccess =
    userProfile?.isActive !== false &&
    (isEditor || (isWorker && userStatus === 'approved'))

  const value = {
    user,
    userProfile,
    userRole,
    userStatus,
    loading,
    error,
    login,
    register,
    signOut,
    sendPasswordReset,
    isSuperAdmin,
    isAdmin,
    isEditor,
    isWorker,
    hasPortalAccess,
    isAuthenticated: !!user,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export default AuthContext
